import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '@/firebase/config';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from 'firebase/auth';
import { auth } from '@/firebase/config';
import { auditLogService } from '@/services/auditLogService';
import { SYSTEM_ROLES, OWNER_ROLES } from '@/constants/roles';
import { isSyntheticStaffAuthEmail, normalizeInviteIdentifier } from '@/utils/staffUsernameAuth';

export interface Invitation {
  id: string;
  email: string;
  name: string;
  role: string; // Keep for backward compatibility
  roles?: string[]; // Support multiple roles
  invitedBy: string;
  organizationId?: string; // Multi-tenant support
  locationIds?: string[]; // Location IDs to assign to user when invitation is accepted
  token: string;
  status: 'pending' | 'accepted' | 'expired';
  createdAt: any;
  expiresAt: any;
  acceptedAt?: any;
}

export interface InvitationData {
  email: string;
  name: string;
  role: string; // Keep for backward compatibility
  roles?: string[]; // Support multiple roles
  invitedBy: string;
  organizationId?: string; // Multi-tenant support
  locationIds?: string[]; // Location IDs to assign to user when invitation is accepted
}

// Generate a secure random token
function generateInvitationToken(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 32; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Build organizationRoles structure from invitation data
 * @param organizationId - Organization ID
 * @param systemRole - System role (single value: 'user', 'manager', 'admin')
 * @param allRoles - All roles array (may contain both system and owner roles)
 * @returns organizationRoles object with nested structure
 */
function buildOrganizationRoles(
  organizationId: string,
  systemRole: string,
  allRoles: string[] = []
): { [key: string]: { systemRole: string; ownerRoles: string[] } } {
  // Filter owner roles from allRoles array
  const ownerRoles = allRoles.filter((role) => OWNER_ROLES.includes(role as any));
  
  // Ensure systemRole is valid (fallback to 'user' if not)
  const validSystemRole = SYSTEM_ROLES.includes(systemRole as any) ? systemRole : 'user';
  
  return {
    [organizationId]: {
      systemRole: validSystemRole,  // SINGLE VALUE
      ownerRoles: ownerRoles        // ARRAY
    }
  };
}

// Create an invitation
export async function createInvitation(invitationData: InvitationData): Promise<{
  success: boolean;
  invitationId?: string;
  /** Present when success; use with getAcceptInvitationUrl for manual sharing (staff usernames). */
  inviteToken?: string;
  error?: string;
}> {
  try {
    const resolved = normalizeInviteIdentifier(invitationData.email);
    if (resolved.error || !resolved.authEmail) {
      return { success: false, error: resolved.error || 'Invalid email or username' };
    }
    const inviteAuthEmail = resolved.authEmail;

    console.log('📧 Creating invitation for:', inviteAuthEmail);

    // Check if user already exists in the system
    const usersQuery = query(
      collection(db, 'users'),
      where('email', '==', inviteAuthEmail)
    );
    const existingUsers = await getDocs(usersQuery);
    
    if (!existingUsers.empty) {
      // Check if user is already in the same organization
      const userData = existingUsers.docs[0].data();
      const userOrgIds = userData.organizationIds || [];
      
      // If user has a single organizationId (backward compatibility), check it too
      if (userData.organizationId && !userOrgIds.includes(userData.organizationId)) {
        userOrgIds.push(userData.organizationId);
      }
      
      if (userOrgIds.includes(invitationData.organizationId) || 
          userData.organizationId === invitationData.organizationId) {
        console.log('⚠️ User already exists in this organization');
        return { success: false, error: 'User is already a member of this organization' };
      }
      // User exists but in different organization - allow invitation
      console.log('✅ User exists in different organization, allowing invitation to new organization');
    }
    
    // Check for pending invitations for the same organization only
    // Allow multiple pending invitations for different organizations
    const existingInvitationQuery = query(
      collection(db, 'invitations'),
      where('email', '==', inviteAuthEmail),
      where('status', '==', 'pending'),
      ...(invitationData.organizationId ? [where('organizationId', '==', invitationData.organizationId)] : [])
    );
    const existingInvitations = await getDocs(existingInvitationQuery);
    
    if (!existingInvitations.empty) {
      console.log('⚠️ Pending invitation already exists for this email and organization');
      return { success: false, error: 'A pending invitation already exists for this email address and organization' };
    }
    
    // Generate invitation token
    const token = generateInvitationToken();
    
    // Set expiration date (7 days from now)
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);
    
    // Create invitation document
    const invitationDoc = await addDoc(collection(db, 'invitations'), {
      email: inviteAuthEmail,
      name: invitationData.name,
      role: invitationData.role, // Keep for backward compatibility
      ...(invitationData.roles && { roles: invitationData.roles }), // Store roles array if provided
      invitedBy: invitationData.invitedBy,
      ...(invitationData.organizationId && { organizationId: invitationData.organizationId }),
      ...(invitationData.locationIds && invitationData.locationIds.length > 0 && { locationIds: invitationData.locationIds }),
      token: token,
      status: 'pending',
      createdAt: serverTimestamp(),
      expiresAt: expiresAt
    });
    
    console.log('✅ Invitation created:', invitationDoc.id);
    return { success: true, invitationId: invitationDoc.id, inviteToken: token };
    
  } catch (error: any) {
    console.error('❌ Error creating invitation:', error);
    return { success: false, error: error.message };
  }
}

// Get invitation by token
export async function getInvitationByToken(token: string): Promise<{ success: boolean; invitation?: Invitation; error?: string }> {
  try {
    console.log('🔍 Looking up invitation with token:', token);
    
    const invitationQuery = query(
      collection(db, 'invitations'),
      where('token', '==', token)
    );
    const querySnapshot = await getDocs(invitationQuery);
    
    if (querySnapshot.empty) {
      console.log('❌ No invitation found with this token');
      return { success: false, error: 'Invalid invitation link' };
    }
    
    const invitationDoc = querySnapshot.docs[0];
    const invitation = { id: invitationDoc.id, ...invitationDoc.data() } as Invitation;
    
    // Check if invitation is expired
    const now = new Date();
    const expiresAt = invitation.expiresAt.toDate ? invitation.expiresAt.toDate() : new Date(invitation.expiresAt);
    
    if (now > expiresAt) {
      console.log('❌ Invitation has expired');
      // Don't update the invitation status here - that requires authentication
      // The admin can manually clean up expired invitations
      return { success: false, error: 'This invitation has expired' };
    }
    
    // Check if already accepted
    if (invitation.status === 'accepted') {
      console.log('❌ Invitation already accepted');
      return { success: false, error: 'This invitation has already been accepted' };
    }
    
    console.log('✅ Valid invitation found:', invitation.email);
    return { success: true, invitation };
    
  } catch (error: any) {
    console.error('❌ Error getting invitation:', error);
    return { success: false, error: error.message };
  }
}

// Process invitation for existing user (when they sign in with invitation token)
export async function processInvitationForExistingUser(token: string, userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('🎯 Processing invitation for existing user:', userId);
    console.log('📝 Token:', token);
    
    // Get invitation
    const invitationResult = await getInvitationByToken(token);
    if (!invitationResult.success || !invitationResult.invitation) {
      console.error('❌ Failed to get invitation:', invitationResult.error);
      return { success: false, error: invitationResult.error };
    }
    
    const invitation = invitationResult.invitation;
    console.log('✅ Got invitation:', {
      id: invitation.id,
      email: invitation.email,
      name: invitation.name,
      status: invitation.status,
      organizationId: invitation.organizationId
    });
    
    // Check if invitation is already accepted
    if (invitation.status === 'accepted') {
      console.log('⚠️ Invitation already accepted, checking if organization is already added');
      // Still process to ensure organization is in the array
    }
    
    // Get user document
    console.log('🔍 Checking for user document in Firestore, UID:', userId);
    const userDocRef = doc(db, 'users', userId);
    const existingUserDoc = await getDoc(userDocRef);
    console.log('📄 User document exists?', existingUserDoc.exists());
    
    // If user document doesn't exist, create it with invitation data
    if (!existingUserDoc.exists()) {
      console.log('⚠️ User document not found, creating it with invitation data...');
      console.log('📝 User ID:', userId);
      console.log('📝 Invitation data:', {
        email: invitation.email,
        name: invitation.name,
        role: invitation.role,
        roles: invitation.roles,
        organizationId: invitation.organizationId
      });
      
      try {
        const userRoles = invitation.roles || [invitation.role];
        const primaryRole = invitation.role;
        
        // Get Firebase Auth user to get email if needed
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        const firebaseUser = auth.currentUser;
        
        const userData: any = {
          id: userId,
          name: invitation.name,
          email: invitation.email || firebaseUser?.email || '',
          role: primaryRole,  // Keep for backward compatibility
          roles: userRoles,   // Keep for backward compatibility
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          authProvider: 'email',
          invited: true,
          verified_email: true // Always set to true for invited users
        };
        
        // Add organization data if available
        if (invitation.organizationId) {
          userData.organizationId = invitation.organizationId;
          userData.organizationIds = [invitation.organizationId];
          
          // Create organizationRoles structure with new nested format
          userData.organizationRoles = buildOrganizationRoles(
            invitation.organizationId,
            primaryRole,
            userRoles
          );
        }
        
        // Add locationIds from invitation if available
        if (invitation.locationIds && invitation.locationIds.length > 0) {
          userData.locationIds = invitation.locationIds;
        }
        
        console.log('📝 Creating user document with data:', userData);
        await setDoc(userDocRef, userData);
        
        // Verify document was created
        const verifyDoc = await getDoc(userDocRef);
        if (!verifyDoc.exists()) {
          console.error('❌ ERROR: Document creation failed - document does not exist after setDoc');
          throw new Error('Failed to create user document in Firestore');
        }
        
        console.log('✅ Created user document from invitation data');
        console.log('✅ Verified document exists:', verifyDoc.id);
        
        // Mark invitation as accepted
        await updateDoc(doc(db, 'invitations', invitation.id), {
          status: 'accepted',
          acceptedAt: serverTimestamp()
        });
        
        console.log('✅ Invitation marked as accepted');
        return { success: true };
      } catch (docError: any) {
        console.error('❌ ERROR creating user document:', docError);
        console.error('❌ Error details:', {
          code: docError.code,
          message: docError.message,
          stack: docError.stack
        });
        // Re-throw to be caught by outer try-catch
        throw new Error(`Failed to create user document: ${docError.message}`);
      }
    }
    
    const existingUserData = existingUserDoc.data();
    
    // Verify email matches
    if (existingUserData.email?.toLowerCase() !== invitation.email.toLowerCase()) {
      return { success: false, error: 'Invitation email does not match user email' };
    }
    
    console.log('📋 Current user data:', {
      organizationId: existingUserData.organizationId,
      organizationIds: existingUserData.organizationIds,
      hasOrganizationIds: !!existingUserData.organizationIds
    });
    
    // Get existing organizationIds array or create new one
    let existingOrgIds = existingUserData.organizationIds || [];
    
    // Backward compatibility: If user has a single organizationId field but no organizationIds array,
    // or if organizationId exists but is not in the array, add it to the array
    if (existingUserData.organizationId) {
      if (!existingOrgIds.includes(existingUserData.organizationId)) {
        existingOrgIds.push(existingUserData.organizationId);
        console.log('✅ Added existing organizationId to array (backward compatibility):', existingUserData.organizationId);
      } else {
        console.log('ℹ️ Existing organizationId already in array:', existingUserData.organizationId);
      }
    }
    
    // Add new organization from invitation to the array if it's not already there
    if (invitation.organizationId) {
      if (!existingOrgIds.includes(invitation.organizationId)) {
        existingOrgIds.push(invitation.organizationId);
        console.log('✅ Added new organization from invitation to user\'s organization list:', invitation.organizationId);
      } else {
        console.log('⚠️ Organization from invitation already in user\'s list:', invitation.organizationId);
      }
    }
    
    console.log('📋 Final organizationIds array (will be saved):', existingOrgIds);
    
    // Build organizationRoles structure for the new organization
    let organizationRoles: { [key: string]: { systemRole: string; ownerRoles: string[] } } = {};
    
    // Get existing organizationRoles if any
    if (existingUserData.organizationRoles && typeof existingUserData.organizationRoles === 'object') {
      organizationRoles = { ...existingUserData.organizationRoles };
    }
    
    // Add roles for the new organization from invitation
    if (invitation.organizationId) {
      const userRoles = invitation.roles || [invitation.role];
      const primaryRole = invitation.role;
      
      organizationRoles[invitation.organizationId] = {
        systemRole: SYSTEM_ROLES.includes(primaryRole as any) ? primaryRole : 'user',
        ownerRoles: userRoles.filter((role) => OWNER_ROLES.includes(role as any))
      };
    }
    
    // Update user document
    const updateData: any = {
      organizationIds: existingOrgIds, // Store all organization IDs
      updatedAt: serverTimestamp()
    };
    
    // Only update organizationRoles if we have at least one organization
    if (Object.keys(organizationRoles).length > 0) {
      updateData.organizationRoles = organizationRoles;
    }
    
    // Add locationIds from invitation if available
    // Preserve existing locations from other organizations, and add new ones from invitation
    if (invitation.locationIds && invitation.locationIds.length > 0) {
      const existingLocationIds = existingUserData.locationIds || [];
      // Merge: keep existing locations that don't belong to current organization, add invitation locations
      // For now, we'll append invitation locations (handling organization-specific filtering happens elsewhere)
      const mergedLocationIds = [...new Set([...existingLocationIds, ...invitation.locationIds])];
      updateData.locationIds = mergedLocationIds;
    }
    
    await updateDoc(userDocRef, updateData);
    
    // Mark invitation as accepted if not already
    if (invitation.status !== 'accepted') {
      await updateDoc(doc(db, 'invitations', invitation.id), {
        status: 'accepted',
        acceptedAt: serverTimestamp()
      });
      console.log('✅ Invitation marked as accepted');
      
      // Log audit entry for invitation acceptance
      try {
        const inviterDoc = await getDoc(doc(db, 'users', invitation.invitedBy));
        const inviterData = inviterDoc.exists() ? inviterDoc.data() : null;
        
        await auditLogService.log({
          userId: userId,
          action: 'invitation_accepted',
          performedBy: invitation.invitedBy,
          performedByName: inviterData?.name || 'Unknown',
          performedByEmail: inviterData?.email || 'Unknown',
          organizationId: invitation.organizationId,
          metadata: {
            invitationId: invitation.id,
            invitedEmail: invitation.email,
            invitedName: invitation.name,
            role: invitation.role,
            roles: invitation.roles,
          },
        });
      } catch (auditError) {
        console.error('Error logging audit for invitation acceptance:', auditError);
        // Don't fail the operation if audit logging fails
      }
    }
    
    console.log('✅ Successfully processed invitation for existing user');
    return { success: true };
    
  } catch (error: any) {
    console.error('❌ Error processing invitation for existing user:', error);
    return { success: false, error: error.message };
  }
}

// Accept invitation and create user account
export async function acceptInvitation(token: string, password: string): Promise<{ success: boolean; userId?: string; error?: string; email?: string }> {
  try {
    console.log('🎯 Accepting invitation with token:', token);
    
    // Get invitation
    const invitationResult = await getInvitationByToken(token);
    if (!invitationResult.success || !invitationResult.invitation) {
      return { success: false, error: invitationResult.error };
    }
    
    const invitation = invitationResult.invitation;
    
    // Check if Firebase Auth account already exists for this email
    let firebaseUser;
    try {
      // Try to create a new account first
      const userCredential = await createUserWithEmailAndPassword(auth, invitation.email, password);
      firebaseUser = userCredential.user;
      console.log('✅ New Firebase Auth account created');
    } catch (createError: any) {
      // If account already exists, try to sign in with the provided password
      if (createError.code === 'auth/email-already-in-use') {
        console.log('⚠️ Firebase Auth account already exists, attempting to sign in...');
        
        try {
          const { signInWithEmailAndPassword } = await import('firebase/auth');
          const userCredential = await signInWithEmailAndPassword(auth, invitation.email, password);
          firebaseUser = userCredential.user;
          console.log('✅ Signed in to existing Firebase Auth account');
        } catch (signInError: any) {
          if (signInError.code === 'auth/wrong-password' || signInError.code === 'auth/invalid-credential') {
            // Return special error code to trigger password reset flow
            return { 
              success: false, 
              error: 'ACCOUNT_EXISTS_NEEDS_RESET', // Special error code
              email: invitation.email // Include email for password reset
            };
          }
          return { success: false, error: `Failed to sign in: ${signInError.message}` };
        }
      } else {
        // Re-throw other errors
        throw createError;
      }
    }
    
    // Update user profile
    await updateProfile(firebaseUser, {
      displayName: invitation.name
    });
    
    // NOTE: We do NOT send email verification for invited users because:
    // 1. The invitation email already verified email ownership (user clicked the link)
    // 2. Sending another verification email would be redundant and confusing
    // 3. The invitation acceptance itself serves as email verification
    // If email verification is needed for other features, we can handle it via Cloud Functions
    // or use the invitation acceptance as proof of email ownership
    
    // Check if user document already exists in Firestore
    // IMPORTANT: For existing Firebase Auth users, we MUST ensure the Firestore document exists
    // This fixes the issue where user exists in Auth but document was deleted/missing
    const { getDoc } = await import('firebase/firestore');
    const userDocRef = doc(db, 'users', firebaseUser.uid);
    const existingUserDoc = await getDoc(userDocRef);
    
    // If document doesn't exist but user is in Firebase Auth, create it now
    // This handles the case where invitation was accepted but document creation failed
    if (!existingUserDoc.exists()) {
      console.log('⚠️ User exists in Firebase Auth but Firestore document is missing - creating it now...');
      console.log('📝 User UID:', firebaseUser.uid);
      console.log('📝 Invitation data:', {
        email: invitation.email,
        name: invitation.name,
        role: invitation.role,
        roles: invitation.roles,
        organizationId: invitation.organizationId
      });
      
      try {
        const userRoles = invitation.roles || [invitation.role];
        const primaryRole = invitation.role;
        
        const userData: any = {
          id: firebaseUser.uid,
          name: invitation.name,
          email: invitation.email,
          role: primaryRole,  // Keep for backward compatibility
          roles: userRoles,   // Keep for backward compatibility
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          authProvider: 'email',
          invited: true,
          verified_email: true // Always true for invited users
        };
        
        // Add organization data if available
        if (invitation.organizationId) {
          userData.organizationId = invitation.organizationId;
          userData.organizationIds = [invitation.organizationId];
          
          // Create organizationRoles structure with new nested format
          userData.organizationRoles = buildOrganizationRoles(
            invitation.organizationId,
            primaryRole,
            userRoles
          );
        }
        
        // Add locationIds from invitation if available
        if (invitation.locationIds && invitation.locationIds.length > 0) {
          userData.locationIds = invitation.locationIds;
        }
        
        console.log('📝 Creating user document with data:', userData);
        await setDoc(userDocRef, userData);
        
        // Verify document was created
        const verifyDoc = await getDoc(userDocRef);
        if (!verifyDoc.exists()) {
          console.error('❌ ERROR: Document creation failed - document does not exist after setDoc');
          throw new Error('Failed to create user document in Firestore');
        }
        
        console.log('✅ Created missing Firestore document for existing Firebase Auth user');
        console.log('✅ Verified document exists:', verifyDoc.id);
        
        // Mark invitation as accepted and return
        await updateDoc(doc(db, 'invitations', invitation.id), {
          status: 'accepted',
          acceptedAt: serverTimestamp()
        });
        
        console.log('✅ Invitation marked as accepted');
        return { success: true, userId: firebaseUser.uid };
      } catch (docError: any) {
        console.error('❌ ERROR creating user document:', docError);
        console.error('❌ Error details:', {
          code: docError.code,
          message: docError.message,
          stack: docError.stack
        });
        // Re-throw to be caught by outer try-catch
        throw new Error(`Failed to create user document: ${docError.message}`);
      }
    }
    
    // Use roles array if available, otherwise fall back to single role
    const userRoles = invitation.roles || [invitation.role];
    const primaryRole = invitation.role; // Keep for backward compatibility
    
    if (existingUserDoc.exists()) {
      // User document exists - add new organization to their list instead of overwriting
      console.log('📝 Updating existing user document with invitation details');
      const existingUserData = existingUserDoc.data();
      
      // Get existing organizationIds array or create new one
      const existingOrgIds = existingUserData.organizationIds || [];
      
      // If user has a single organizationId (backward compatibility), add it to array
      if (existingUserData.organizationId && !existingOrgIds.includes(existingUserData.organizationId)) {
        existingOrgIds.push(existingUserData.organizationId);
      }
      
      // Add new organization to the array if it's not already there
      if (invitation.organizationId && !existingOrgIds.includes(invitation.organizationId)) {
        existingOrgIds.push(invitation.organizationId);
        console.log('✅ Added new organization to user\'s organization list:', invitation.organizationId);
      }
      
      // Build or update organizationRoles structure
      let organizationRoles: { [key: string]: { systemRole: string; ownerRoles: string[] } } = {};
      
      // Get existing organizationRoles if any
      if (existingUserData.organizationRoles && typeof existingUserData.organizationRoles === 'object') {
        organizationRoles = { ...existingUserData.organizationRoles };
      }
      
      // Add/update roles for the new organization from invitation
      if (invitation.organizationId) {
        const ownerRoles = userRoles.filter((role) => OWNER_ROLES.includes(role as any));
        const validSystemRole = SYSTEM_ROLES.includes(primaryRole as any) ? primaryRole : 'user';
        
        organizationRoles[invitation.organizationId] = {
          systemRole: validSystemRole,
          ownerRoles: ownerRoles
        };
      }
      
      // Update user document - ensure verified_email is always set to true for invited users
      const updateData: any = {
        name: invitation.name || existingUserData.name,
        email: invitation.email || existingUserData.email || firebaseUser.email,
        role: primaryRole || existingUserData.role, // Keep for backward compatibility
        roles: userRoles.length > 0 ? userRoles : (existingUserData.roles || [existingUserData.role].filter(Boolean)), // Store roles array
        organizationIds: existingOrgIds.length > 0 ? existingOrgIds : (existingUserData.organizationIds || []), // Store all organization IDs
        // Keep existing organizationId for backward compatibility (don't overwrite current org)
        // Only set if user doesn't have one yet
        ...(invitation.organizationId && !existingUserData.organizationId && { organizationId: invitation.organizationId }),
        updatedAt: serverTimestamp(),
        invited: true,
        verified_email: true // Always set to true for invited users
      };
      
      // Only update organizationRoles if we have at least one organization
      if (Object.keys(organizationRoles).length > 0) {
        updateData.organizationRoles = organizationRoles;
      }
      
      // Add locationIds from invitation if available
      // Preserve existing locations from other organizations, and add new ones from invitation
      if (invitation.locationIds && invitation.locationIds.length > 0) {
        const existingLocationIds = existingUserData.locationIds || [];
        // Merge: keep existing locations that don't belong to current organization, add invitation locations
        // For now, we'll append invitation locations (handling organization-specific filtering happens elsewhere)
        const mergedLocationIds = [...new Set([...existingLocationIds, ...invitation.locationIds])];
        updateData.locationIds = mergedLocationIds;
      }
      
      await updateDoc(userDocRef, updateData);
      
      // Ensure user document has all required fields if it was missing some
      if (!existingUserData.verified_email) {
        console.log('⚠️ User document was missing verified_email, now fixed');
      }
      
      // Log audit entry for invitation acceptance (existing user)
      try {
        const inviterDoc = await getDoc(doc(db, 'users', invitation.invitedBy));
        const inviterData = inviterDoc.exists() ? inviterDoc.data() : null;
        
        await auditLogService.log({
          userId: firebaseUser.uid,
          action: 'invitation_accepted',
          performedBy: invitation.invitedBy,
          performedByName: inviterData?.name || 'Unknown',
          performedByEmail: inviterData?.email || 'Unknown',
          organizationId: invitation.organizationId,
          metadata: {
            invitationId: invitation.id,
            invitedEmail: invitation.email,
            invitedName: invitation.name,
            role: primaryRole,
            roles: userRoles,
          },
        });
      } catch (auditError) {
        console.error('Error logging audit for invitation acceptance:', auditError);
        // Don't fail the operation if audit logging fails
      }
    } else {
      // Create new user document in Firestore using UID as document ID
      const newUserData: any = {
        id: firebaseUser.uid,
        name: invitation.name,
        email: invitation.email,
        role: primaryRole, // Keep for backward compatibility
        roles: userRoles, // Store roles array
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        authProvider: 'email',
        invited: true, // Mark user as invited - email is implicitly verified via invitation acceptance
        verified_email: true // Treat invited users as verified since invitation email already verified ownership
      };
      
      // Add organization data if available
      if (invitation.organizationId) {
        newUserData.organizationId = invitation.organizationId;
        newUserData.organizationIds = [invitation.organizationId];
        
        // Create organizationRoles structure with new nested format
        newUserData.organizationRoles = buildOrganizationRoles(
          invitation.organizationId,
          primaryRole,
          userRoles
        );
      }
      
      // Add locationIds from invitation if available
      if (invitation.locationIds && invitation.locationIds.length > 0) {
        newUserData.locationIds = invitation.locationIds;
      }
      
      await setDoc(userDocRef, newUserData);
      
      // Log audit entry for user creation
      try {
        const inviterDoc = await getDoc(doc(db, 'users', invitation.invitedBy));
        const inviterData = inviterDoc.exists() ? inviterDoc.data() : null;
        
        await auditLogService.log({
          userId: firebaseUser.uid,
          action: 'user_created',
          performedBy: invitation.invitedBy,
          performedByName: inviterData?.name || 'Unknown',
          performedByEmail: inviterData?.email || 'Unknown',
          organizationId: invitation.organizationId,
          metadata: {
            invitationId: invitation.id,
            role: primaryRole,
            roles: userRoles,
          },
        });
      } catch (auditError) {
        console.error('Error logging audit for user creation:', auditError);
        // Don't fail the operation if audit logging fails
      }
    }
    
    // Mark invitation as accepted
    await updateDoc(doc(db, 'invitations', invitation.id), {
      status: 'accepted',
      acceptedAt: serverTimestamp()
    });
    
    console.log('✅ User account created and invitation accepted:', firebaseUser.uid);
    return { success: true, userId: firebaseUser.uid };
    
  } catch (error: any) {
    console.error('❌ Error accepting invitation:', error);
    
    // Handle specific Firebase Auth errors
    if (error.code === 'auth/email-already-in-use') {
      return { success: false, error: 'An account with this email already exists' };
    } else if (error.code === 'auth/weak-password') {
      return { success: false, error: 'Password is too weak. Please choose a stronger password' };
    }
    
    return { success: false, error: error.message };
  }
}

// Get all invitations (for admin)
export async function getAllInvitations(): Promise<{ success: boolean; invitations?: Invitation[]; error?: string }> {
  try {
    const invitationsQuery = query(
      collection(db, 'invitations'),
      where('status', '!=', 'expired')
    );
    const querySnapshot = await getDocs(invitationsQuery);
    
    const invitations: Invitation[] = [];
    querySnapshot.forEach((doc) => {
      invitations.push({ id: doc.id, ...doc.data() } as Invitation);
    });
    
    return { success: true, invitations };
    
  } catch (error: any) {
    console.error('❌ Error getting invitations:', error);
    return { success: false, error: error.message };
  }
}

// Get pending invitations for an organization (invite sent but not yet accepted)
export async function getInvitationsByOrganization(organizationId: string): Promise<{ success: boolean; invitations?: Invitation[]; error?: string }> {
  try {
    const invitationsQuery = query(
      collection(db, 'invitations'),
      where('organizationId', '==', organizationId),
      where('status', '==', 'pending')
    );
    const querySnapshot = await getDocs(invitationsQuery);
    
    const invitations: Invitation[] = [];
    const now = new Date();
    
    querySnapshot.forEach((doc) => {
      const invitation = { id: doc.id, ...doc.data() } as Invitation;
      // Filter out expired invitations
      const expiresAt = invitation.expiresAt?.toDate ? invitation.expiresAt.toDate() : new Date(invitation.expiresAt || 0);
      if (now <= expiresAt) {
        invitations.push(invitation);
      }
    });
    
    return { success: true, invitations };
    
  } catch (error: any) {
    console.error('❌ Error getting invitations by organization:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Admin-only: complete a staff-username invitation server-side (creates Firebase Auth user + profile).
 * Does not sign out the admin. Optional password; if omitted, server generates one and returns it once.
 */
export async function completeStaffInvitationAsAdmin(
  invitationId: string,
  password?: string
): Promise<{
  success: boolean;
  userId?: string;
  existingAccount?: boolean;
  initialPassword?: string | null;
  error?: string;
}> {
  try {
    const { httpsCallable, getFunctions } = await import('firebase/functions');
    const functions = getFunctions();
    const fn = httpsCallable<
      { invitationId: string; password?: string },
      {
        success: boolean;
        userId: string;
        existingAccount: boolean;
        initialPassword: string | null;
      }
    >(functions, 'completeStaffInvitationAsAdmin');
    const result = await fn({
      invitationId,
      ...(password && password.trim().length > 0 ? { password: password.trim() } : {}),
    });
    const d = result.data;
    if (!d?.success) {
      return { success: false, error: 'Request failed' };
    }
    return {
      success: true,
      userId: d.userId,
      existingAccount: d.existingAccount,
      initialPassword: d.initialPassword,
    };
  } catch (e: any) {
    const msg =
      typeof e?.message === 'string'
        ? e.message.replace(/^.*?:\s*/, '')
        : e?.code
          ? String(e.code)
          : 'Failed to complete invitation';
    return { success: false, error: msg };
  }
}

// Cancel invitation
export async function cancelInvitation(invitationId: string): Promise<{ success: boolean; error?: string }> {
  try {
    await deleteDoc(doc(db, 'invitations', invitationId));
    console.log('✅ Invitation cancelled:', invitationId);
    return { success: true };
    
  } catch (error: any) {
    console.error('❌ Error cancelling invitation:', error);
    return { success: false, error: error.message };
  }
}

// Resend invitation email
export async function resendInvitation(invitationId: string): Promise<{ success: boolean; error?: string; warning?: string }> {
  try {
    console.log('📧 Resending invitation:', invitationId);
    
    // Get the invitation document
    const invitationDoc = await getDoc(doc(db, 'invitations', invitationId));
    if (!invitationDoc.exists()) {
      return { success: false, error: 'Invitation not found' };
    }
    
    const invitation = invitationDoc.data() as Invitation;
    
    // Check if invitation is already accepted
    if (invitation.status === 'accepted') {
      return { success: false, error: 'Cannot resend an accepted invitation' };
    }

    const newExpiresAt = new Date();
    newExpiresAt.setDate(newExpiresAt.getDate() + 7); // 7 days from now

    // Staff usernames have no inbox — extend expiry only; keep the same token so shared links still work
    if (isSyntheticStaffAuthEmail(invitation.email)) {
      await updateDoc(doc(db, 'invitations', invitationId), {
        expiresAt: newExpiresAt,
        status: 'pending',
      });
      return {
        success: true,
        warning:
          'No email is sent for staff usernames. Use “Copy invite link” in Pending Invitations and share it (text, QR, etc.).',
      };
    }

    // Generate new token and update expiration (real email inboxes)
    const newToken = generateInvitationToken();

    // Update invitation with new token and expiration
    await updateDoc(doc(db, 'invitations', invitationId), {
      token: newToken,
      expiresAt: newExpiresAt,
      status: 'pending', // Reset to pending if it was expired
      createdAt: serverTimestamp() // Update creation time to trigger email
    });
    
    // Call Cloud Function to resend email
    let emailSent = false;
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { getFunctions } = await import('firebase/functions');
      const functions = getFunctions();
      const resendInvitationEmail = httpsCallable(functions, 'resendInvitationEmail');
      
      await resendInvitationEmail({ invitationId });
      emailSent = true;
      console.log('✅ Invitation email resent via Cloud Function');
    } catch (cloudFunctionError: any) {
      console.warn('⚠️ Cloud Function call failed, invitation updated but email may not be sent:', cloudFunctionError);
      
      // Check if it's a "function not found" or "internal" error (likely not deployed)
      if (cloudFunctionError?.code === 'functions/not-found' || 
          cloudFunctionError?.code === 'internal' ||
          cloudFunctionError?.message?.includes('not found')) {
        console.warn('⚠️ Cloud Function may not be deployed yet. Please deploy Firebase Functions.');
        // Return success with warning that email may not have been sent
        return { 
          success: true, 
          warning: 'Invitation updated but email may not have been sent. Please deploy Firebase Functions or send the invitation link manually.' 
        };
      }
      // For other errors, still return success but note email may not have been sent
      return { 
        success: true, 
        warning: 'Invitation updated but email sending failed. Please check Firebase Functions logs.' 
      };
    }
    
    console.log('✅ Invitation resent:', invitationId);
    return { success: true };
    
  } catch (error: any) {
    console.error('❌ Error resending invitation:', error);
    return { success: false, error: error.message };
  }
}

// Get pending invitations for a user by email
export async function getPendingInvitationsForEmail(email: string): Promise<{ success: boolean; invitations?: Invitation[]; error?: string }> {
  try {
    console.log('🔍 Looking up pending invitations for email:', email);
    
    const invitationQuery = query(
      collection(db, 'invitations'),
      where('email', '==', email.toLowerCase()),
      where('status', '==', 'pending')
    );
    const querySnapshot = await getDocs(invitationQuery);
    
    const invitations: Invitation[] = [];
    querySnapshot.forEach((doc) => {
      const invitation = { id: doc.id, ...doc.data() } as Invitation;
      
      // Check if invitation is expired
      const now = new Date();
      const expiresAt = invitation.expiresAt.toDate ? invitation.expiresAt.toDate() : new Date(invitation.expiresAt);
      
      if (now <= expiresAt) {
        invitations.push(invitation);
      }
    });
    
    console.log(`✅ Found ${invitations.length} pending invitation(s) for ${email}`);
    return { success: true, invitations };
    
  } catch (error: any) {
    console.error('❌ Error getting pending invitations:', error);
    return { success: false, error: error.message };
  }
}

// Process all pending invitations for an existing user
export async function processPendingInvitationsForUser(userId: string, userEmail: string): Promise<{ success: boolean; processedCount?: number; error?: string }> {
  try {
    console.log('🔍 Checking for pending invitations for user:', userId, userEmail);
    
    // Get all pending invitations for this email
    const invitationsResult = await getPendingInvitationsForEmail(userEmail);
    if (!invitationsResult.success || !invitationsResult.invitations || invitationsResult.invitations.length === 0) {
      console.log('ℹ️ No pending invitations found');
      return { success: true, processedCount: 0 };
    }
    
    // Get user document
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      return { success: false, error: 'User document not found' };
    }
    
    const userData = userDoc.data();
    
    // Get existing organizationIds array or create new one
    let existingOrgIds = userData.organizationIds || [];
    
    // Backward compatibility: If user has a single organizationId, add it to array
    if (userData.organizationId && !existingOrgIds.includes(userData.organizationId)) {
      existingOrgIds.push(userData.organizationId);
      console.log('✅ Added existing organizationId to array (backward compatibility):', userData.organizationId);
    }
    
    let processedCount = 0;
    
    // Process each pending invitation
    for (const invitation of invitationsResult.invitations) {
      if (invitation.organizationId && !existingOrgIds.includes(invitation.organizationId)) {
        existingOrgIds.push(invitation.organizationId);
        processedCount++;
        console.log('✅ Added organization from pending invitation:', invitation.organizationId);
        
        // Mark invitation as accepted
        await updateDoc(doc(db, 'invitations', invitation.id), {
          status: 'accepted',
          acceptedAt: serverTimestamp()
        });
      }
    }
    
    if (processedCount > 0) {
      // Update user document with all organizations
      await updateDoc(userDocRef, {
        organizationIds: existingOrgIds,
        updatedAt: serverTimestamp()
      });
      console.log(`✅ Processed ${processedCount} pending invitation(s), updated organizationIds array`);
    }
    
    return { success: true, processedCount };
    
  } catch (error: any) {
    console.error('❌ Error processing pending invitations:', error);
    return { success: false, error: error.message };
  }
}
